import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import UploadPage from "./pages/UploadPage.jsx";

// Each page is its own chunk, so a client downloads only the page they opened
// — and pdf.js, pdf-lib or jsPDF only on the page that uses it.
const SignScopePage = lazy(() => import("./pages/SignScopePage.jsx"));
const SignOnlyPage = lazy(() => import("./pages/SignOnlyPage.jsx"));
const SoaFormPage = lazy(() => import("./pages/SoaFormPage.jsx"));
const PdfSignPage = lazy(() => import("./pages/PdfSignPage.jsx"));
const ReadPage = lazy(() => import("./pages/ReadPage.jsx"));

/** An old `.html` address: the same page at its own path, query string kept. */
function Moved({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/** `/f/<slug>` (business-card QR codes): the public form, `?agent=` and the rest kept. */
function FormShortLink() {
  const { slug } = useParams();
  const { search } = useLocation();
  const next = new URLSearchParams(search);
  next.set("form", slug);
  return <Navigate to={`/soa?${next.toString()}`} replace />;
}

/**
 * Every address a link has ever used, mapped to its page. Links already in
 * clients' inboxes and on printed QR codes must keep working:
 *
 *   /<token>, /?form=<slug>                 upload documents
 *   /sign?token=<t>, /sign/<t>              sign a Scope of Appointment
 *   /sign-only/<t>?data=…                   a bare signature pad
 *   /soa?form=<slug>&agent=<slug>, /f/<slug> public form
 *   /pdf-sign?token=<t>, /pdf-sign?form=<s> fill in and sign a PDF
 *   /read?token=<t>, /read/<t>              a secure message
 */
export default function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<UploadPage />} />
        <Route path="/sign" element={<SignScopePage />} />
        <Route path="/sign/*" element={<SignScopePage />} />
        <Route path="/sign-only/*" element={<SignOnlyPage />} />
        <Route path="/soa" element={<SoaFormPage />} />
        <Route path="/soa/*" element={<SoaFormPage />} />
        <Route path="/f/:slug" element={<FormShortLink />} />
        <Route path="/pdf-sign" element={<PdfSignPage />} />
        <Route path="/read" element={<ReadPage />} />
        <Route path="/read/*" element={<ReadPage />} />
        <Route path="/index.html" element={<Moved to="/" />} />
        <Route path="/sign.html" element={<Moved to="/sign" />} />
        <Route path="/sign-only.html" element={<Moved to="/sign-only" />} />
        <Route path="/soa.html" element={<Moved to="/soa" />} />
        <Route path="/pdf-sign.html" element={<Moved to="/pdf-sign" />} />
        <Route path="/read.html" element={<Moved to="/read" />} />
        <Route path="/:token" element={<UploadPage />} />
        <Route path="*" element={<UploadPage />} />
      </Routes>
    </Suspense>
  );
}
